import React from 'react';
import ButtonDark from '../components/ui/button/buttonDark';
import ButtonLight from '../components/ui/button/buttonLight';
import Badge from '../components/ui/badge/badge';
import { ArrowRight, Calendar, ScanLine, Flame, Activity, Play, ShieldCheck, Award, Stethoscope, Clock, Check, ScanFace, BarChart3, Sun, Layers, Crosshair, Waves } from 'lucide-react';
import { MapPin, Star, ChevronDown, Fingerprint, List, Music2, Phone } from 'lucide-react';
import { CheckCircle } from 'lucide-react';
import Navbar from '../components/navbar';
import ContactForm from '../components/contactForm';

function HomePage() {
  return (
    <>
      <Navbar />
      <main className="w-full overflow-hidden">
    {/* Hero Section */}
    <section className="pt-36 pb-16 px-6 flex flex-col items-center text-center max-w-6xl mx-auto relative">
      {/* Background Decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-slate-100/50 to-transparent blur-3xl -z-10 rounded-full" />
      <Badge className="mb-8">
        Neu in Bad Salzungen: Ceragem Master V4
      </Badge>
      <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold text-slate-900 tracking-tight leading-[1.05] mb-8 max-w-4xl">
        Endlich mehr Lebensqualität.
        <br />
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D06C60] via-[#D06C60]/60 to-[#D06C60]/40">
          Ganz ohne Medikamente.
        </span>
      </h1>
      <p className="text-lg md:text-xl text-slate-500 max-w-2xl leading-relaxed font-normal mb-10">
        Kombiniere die Präzision modernster Medizintechnik mit traditioneller
        fernöstlicher Heilkunde. Der Ceragem Master V4 scannt deine Wirbelsäule
        und bietet eine maßgeschneiderte Behandlung gegen Rückenschmerzen und
        Stress.
      </p>
      <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
        <ButtonDark
          onClick={() => window.location.href = '#preise'}
        >
          Jetzt Angebot sichern
          <ArrowRight className="w-4 h-4" />
        </ButtonDark>
        <ButtonLight
          onClick={() => window.location.href = '#video'}
        >
          <Play className="w-4 h-4 text-slate-400" />
          Video ansehen
        </ButtonLight>
      </div>
      {/* Trust Badges */}
      <div className="mt-16 pt-8 border-t border-slate-100 w-full max-w-3xl">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-6">
          Medizinisch zertifizierte Technologie
        </p>
        <div className="flex flex-wrap justify-center gap-8 md:gap-16 opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
          {/* Simple Text Logos for Trust */}
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            <span className="font-semibold text-slate-900">FDA Zulassung</span>
          </div>
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5" />
            <span className="font-semibold text-slate-900">Patentiert</span>
          </div>
          <div className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5" />
            <span className="font-semibold text-slate-900">Medizinprodukt</span>
          </div>
        </div>
      </div>
    </section>
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <span className="text-[#D06C60] font-semibold tracking-wider text-xs uppercase mb-4 block">
          Physiotherapie Sinn
        </span>
        <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
          Intelligente Thermal-Massage: Deine Auszeit vom Alltagsstress
        </h2>
        <p className="text-lg text-slate-600 leading-relaxed mb-8">
          Fühlst du dich verspannt, gestresst oder plagen dich immer
          wiederkehrende Rückenschmerzen? Gönn deinem Körper eine hochwirksame
          Auszeit. Erlebe bei uns in Bad Salzungen die Ceragem Master V4 – eine
          einzigartige Kombination aus Wirbelsäulen-Scan, sanfter Akupressur und
          tiefenwirksamer Infrarotwärme.
        </p>
        <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
          <h3 className="text-xl font-semibold text-slate-900 mb-4">
            Deine Gesundheitsreise
          </h3>
          <p className="text-slate-500 mb-6">
            Von der ersten tiefenentspannten Minute an bis zur spürbaren
            Lockerung – entdecke eine neue Form der Regeneration, die exakt auf
            deinen Körper zugeschnitten ist.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <ButtonLight
              onClick={() => window.location.href = '#ceragem'}
            >
              Ceragem entdecken
              <ArrowRight className="w-4 h-4" />
            </ButtonLight>
            <ButtonDark
              onClick={() => window.location.href = '#termin'}
            >
              Termin vereinbaren
              <Calendar className="w-4 h-4" />
            </ButtonDark>
          </div>
        </div>
      </div>
    </section>
    <section className="py-20 bg-slate-900 text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#D06C60] opacity-20 blur-[100px] rounded-full" />
      <div className="max-w-7xl mx-auto px-6 relative z-10 flex flex-col md:flex-row items-center gap-12">
        <div className="flex-1">
          <Badge className="mb-6">
            <span className="font-bold text-[#D06C60]">10+</span>
            <span className="font-medium text-slate-300">
              Jahre Erfahrung
            </span>
          </Badge>
          <h2 className="text-3xl md:text-4xl font-semibold mb-6">
            Experten an Deiner Seite
          </h2>
          <p className="text-slate-400 text-lg leading-relaxed mb-8">
            Hinter jeder erfolgreichen Therapie stehen Menschen, die wissen,
            worauf es ankommt. Unser Team aus Bad Salzungen bringt Erfahrung und
            Expertise zusammen, um dich auf deinem individuellen Weg zu
            begleiten.
          </p>
        </div>
        <div className="flex-1 w-full max-w-md">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-800 rounded-2xl h-40 w-full animate-pulse" />
            <div className="bg-slate-800 rounded-2xl h-40 w-full mt-8 animate-pulse" />
          </div>
        </div>
      </div>
    </section>
    {/* Problem / Solution Grid */}
    <section
      id="vorteile"
      className="py-24 bg-slate-50 border-y border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16 md:text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight mb-4">
            Warum herkömmliche Massagen oft nicht reichen
          </h2>
          <p className="text-slate-500 text-lg">
            Jeder Rücken ist so einzigartig wie ein Fingerabdruck.
            Standard-Behandlungen gehen oft nicht tief genug oder verfehlen die
            Problemzonen.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Card 1 */}
          <div className="bg-white p-8 rounded-2xl shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] border border-slate-100 hover:border-[#D06C60]/30 transition-colors group">
            <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#D06C60]/10 transition-colors">
              <ScanLine className="w-6 h-6 text-slate-600 group-hover:text-[#D06C60]" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">
              Individueller Wirbelsäulen-Scan
            </h3>
            <p className="text-slate-500 leading-relaxed text-sm">
              Der Ceragem V4 vermisst deine Wirbelsäule vor jeder Behandlung
              millimetergenau. Die Massage passt sich deiner Länge und Krümmung
              an – für maximale Effektivität.
            </p>
          </div>
          {/* Card 2 */}
          <div className="bg-white p-8 rounded-2xl shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] border border-slate-100 hover:border-[#D06C60]/30 transition-colors group relative overflow-hidden">
            {/* Highlight Badge */}
            <div className="absolute top-0 right-0 bg-[#D06C60] w-16 h-16 transform translate-x-8 -translate-y-8 rotate-45" />
            <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#D06C60]/10 transition-colors">
              <Flame className="w-6 h-6 text-slate-600 group-hover:text-[#D06C60]" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">
              Tiefenwärme bis 65°C
            </h3>
            <p className="text-slate-500 leading-relaxed text-sm">
              Die integrierten Epoxid-Carbon-Platten und Jadesteine erzeugen
              eine intensive Infrarotwärme, die tief in das Muskelgewebe
              eindringt und die Durchblutung massiv fördert.
            </p>
          </div>
          {/* Card 3 */}
          <div className="bg-white p-8 rounded-2xl shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] border border-slate-100 hover:border-[#D06C60]/30 transition-colors group">
            <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#D06C60]/10 transition-colors">
              <Activity className="w-6 h-6 text-slate-600 group-hover:text-[#D06C60]" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">
              12 Massage-Modi
            </h3>
            <p className="text-slate-500 leading-relaxed text-sm">
              Vom "Entspannungsmodus" bis zum intensiven "Tiefenmodus". Wähle
              aus 12 Programmen genau das, was dein Körper heute braucht.
              Intensität in 9 Stufen regelbar.
            </p>
          </div>
        </div>
      </div>
    </section>
    {/* Feature Spotlight (Bento Grid Style) */}
    <section id="ceragem" className="py-24 px-6 max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 auto-rows-[minmax(300px,auto)]">
        {/* Main Feature: Visual */}
        <div className="lg:col-span-7 bg-slate-900 rounded-3xl p-10 flex flex-col justify-between relative overflow-hidden group shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-tr from-[#D06C60]/20 to-transparent opacity-30" />
          {/* Abstract Image/Pattern Representation */}
          <div className="absolute right-0 bottom-0 w-3/4 h-3/4 opacity-40 translate-x-10 translate-y-10">
            <svg
              viewBox="0 0 200 200"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-full fill-slate-700"
            >
              <path
                d="M45.7,-76.3C58.9,-69.3,69.1,-58.3,77.6,-46.3C86.1,-34.3,92.9,-21.3,91.8,-8.6C90.7,4.1,81.7,16.5,73.4,28.6C65.1,40.7,57.5,52.5,47.2,61.9C36.9,71.3,23.9,78.3,9.8,81.4C-4.3,84.5,-19.5,83.7,-33.1,77.7C-46.7,71.7,-58.7,60.5,-68.9,47.8C-79.1,35.1,-87.5,20.9,-88.4,6.2C-89.3,-8.5,-82.7,-23.7,-73.4,-36.8C-64.1,-49.9,-52.1,-60.9,-39.3,-68.2C-26.5,-75.5,-12.9,-79.1,1.5,-81.7C15.9,-84.3,32.5,-83.3,45.7,-76.3Z"
                transform="translate(100 100)"
              />
            </svg>
          </div>
          <div className="relative z-10">
            <span className="text-[#D06C60] font-semibold tracking-wider text-xs uppercase mb-2 block">
              Ceragem Master V4
            </span>
            <h3 className="text-3xl md:text-4xl font-semibold text-white mb-4 tracking-tight">
              Dein Rücken.
              <br />
              Neu definiert.
            </h3>
            <p className="text-slate-300 max-w-md leading-relaxed">
              Erlebe die Kombination aus Akupressur, Chiropraktik, Massage und
              Wärmetherapie. Die Ceragem Liege ist mehr als Wellness – sie ist
              Gesundheitsvorsorge.
            </p>
          </div>
          <div className="relative z-10 mt-8">
            <ButtonLight
              onClick={() => window.location.href = '#video'}
            >
              So funktioniert's
              <ArrowRight className="w-4 h-4" />
            </ButtonLight>
          </div>
        </div>
        {/* Secondary Feature: Convenience */}
        <div className="lg:col-span-5 bg-white border border-slate-200 rounded-3xl p-10 shadow-lg shadow-slate-200/50 flex flex-col justify-center">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
              <Clock className="w-5 h-5" />
            </div>
            <h4 className="text-xl font-semibold text-slate-900">
              Passt in den Alltag
            </h4>
          </div>
          <ul className="space-y-4 text-slate-600">
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#D06C60] mt-0.5 shrink-0" />
              <span>
                <strong className="text-slate-900">
                  Kein Entkleiden nötig:&nbsp;
                </strong>
                Einfach in bequemer Kleidung hinlegen.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#D06C60] mt-0.5 shrink-0" />
              <span>
                <strong className="text-slate-900">Nur 36 Minuten:&nbsp;</strong>
                Eine komplette Einheit dauert kaum länger als eine Kaffeepause.
              </span>
            </li>   
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#D06C60] mt-0.5 shrink-0" />
              <span>
                <strong className="text-slate-900">Sofort spürbar:&nbsp;</strong>
                Viele Patienten berichten schon nach der ersten Anwendung von
                Erleichterung.
              </span>
            </li>
          </ul>
        </div>
      </div>
    </section>
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#D06C60] font-semibold tracking-wider text-xs uppercase mb-4 block">
            Massage in Bad Salzungen und Waldfisch
          </span>
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
            Hightech trifft auf Tiefenentspannung
          </h2>
          <p className="text-slate-500 text-lg">
            Hinter der Ceragem Master V4 steht mehr als nur eine Massageliege.
            Es ist ein zertifiziertes medizinisches Therapiegerät. Unsere
            Möglichkeiten für deine Entspannung:
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-slate-50 p-8 rounded-2xl hover:bg-white hover:shadow-xl hover:shadow-slate-200/50 transition-all border border-transparent hover:border-slate-100 group">
            <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-[#D06C60] group-hover:scale-110 transition-transform">
              <ScanFace className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">
              Individueller Wirbelsäulen-Scan
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              Dein Rücken ist einzigartig. Die Liege scannt deine Wirbelsäule
              vor jeder Anwendung für eine exakte Anpassung.
            </p>
          </div>
          <div className="bg-slate-50 p-8 rounded-2xl hover:bg-white hover:shadow-xl hover:shadow-slate-200/50 transition-all border border-transparent hover:border-slate-100 group">
            <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-[#D06C60] group-hover:scale-110 transition-transform">
              <BarChart3 className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">
              9 Intensitätsstufen
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              Von sanfter Entspannung bis zur kräftigen Lockerung. Du
              entscheidest per Fernbedienung über die Intensität.
            </p>
          </div>
          <div className="bg-slate-50 p-8 rounded-2xl hover:bg-white hover:shadow-xl hover:shadow-slate-200/50 transition-all border border-transparent hover:border-slate-100 group">
            <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-[#D06C60] group-hover:scale-110 transition-transform">
              <Sun className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">
              Tiefenwirksame Infrarotwärme
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              Wohltuende Wärme bis 65°C dringt tief in die Muskulatur ein, löst
              Verspannungen und fördert die Durchblutung.
            </p>
          </div>
          <div className="bg-slate-50 p-8 rounded-2xl hover:bg-white hover:shadow-xl hover:shadow-slate-200/50 transition-all border border-transparent hover:border-slate-100 group">
            <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-[#D06C60] group-hover:scale-110 transition-transform">
              <Layers className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">
              15 Automatik-Programme
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              Wähle aus 15 Programmen – von Ganzkörper-Entspannung bis zu
              fokussierten Anwendungen für Nacken oder Lendenwirbel.
            </p>
          </div>
          <div className="bg-slate-50 p-8 rounded-2xl hover:bg-white hover:shadow-xl hover:shadow-slate-200/50 transition-all border border-transparent hover:border-slate-100 group">
            <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-[#D06C60] group-hover:scale-110 transition-transform">
              <Crosshair className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">
              Gezielter Master-Modus
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              Wähle einen Wirbelbereich gezielt aus und lasse ihn intensiv mit
              Wärme und Akupressur behandeln.
            </p>
          </div>
          <div className="bg-slate-50 p-8 rounded-2xl hover:bg-white hover:shadow-xl hover:shadow-slate-200/50 transition-all border border-transparent hover:border-slate-100 group">
            <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-[#D06C60] group-hover:scale-110 transition-transform">
              <Waves className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">
              Bauch- &amp; Lokalmassage
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              Externes Vibrations- und Wärmegerät zur Entspannung auf dem Bauch
              oder für andere Muskelpartien.
            </p>
          </div>
        </div>
      </div>
    </section>
    <section className="py-24 bg-slate-50 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-semibold text-slate-900 mb-4">
            Noch mehr erfahren?
          </h2>
          <p className="text-slate-500">
            Alle Details zur Technologie im Überblick.
          </p>
        </div>
        <div className="space-y-12">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-700">
                  <ScanLine className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">
                  Automatische Thermalmassage
                </h3>
              </div>
              <ul className="space-y-4">
                <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    Individuelle Anpassung
                  </h4>
                  <p className="text-xs text-slate-500">
                    Patentiertes Spinal Scanning erfasst Länge, Gewicht und
                    Krümmung für personalisierte Massage.
                  </p>
                </li>
                <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    Druckerkennungssystem
                  </h4>
                  <p className="text-xs text-slate-500">
                    Massagerollen passen sich automatisch der Wirbelsäulenkontur
                    an.
                  </p>
                </li>
                <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    Präzise Positionierung
                  </h4>
                  <p className="text-xs text-slate-500">
                    Erkennt individuelle Unterschiede für optimale Ergebnisse.
                  </p>
                </li>
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#D06C60]/10 flex items-center justify-center text-[#D06C60]">
                  <Flame className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">
                  Konzentrierte Infrarotwärme
                </h3>
              </div>
              <ul className="space-y-4">
                <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    Tief eindringende Wärme
                  </h4>
                  <p className="text-xs text-slate-500">
                    Bis zu 65°C entlang der Wirbelsäule durch Ceratonic-Rollen.
                  </p>
                </li>
                <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    Moxa-Effekt
                  </h4>
                  <p className="text-xs text-slate-500">
                    Punktuelle Wärmeanwendung angelehnt an traditionelle
                    chinesische Medizin.
                  </p>
                </li>
                <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    Ganzkörper-Erwärmung
                  </h4>
                  <p className="text-xs text-slate-500">
                    Zusätzliche Heizelemente im Hauptkörper und Zusatzmatte.
                  </p>
                </li>
              </ul>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                  <Fingerprint className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">
                  Akupressur-Massage
                </h3>
              </div>
              <ul className="space-y-4">
                <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    Bioaktive Punkte
                  </h4>
                  <p className="text-xs text-slate-500">
                    Gezielter Druck auf spezifische Punkte durch Heben/Senken
                    der Rollen.
                  </p>
                </li>
                <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    60-Sekunden-Behandlung
                  </h4>
                  <p className="text-xs text-slate-500">
                    Verweilen an Akupressurpunkten für maximale Stimulation.
                  </p>
                </li>
                <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    Wirbelsäulen-Streckung
                  </h4>
                  <p className="text-xs text-slate-500">
                    Sanftes Auseinanderziehen der Wirbel zur Entlastung.
                  </p>
                </li>
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center text-green-600">
                  <List className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">
                  Programmvielfalt
                </h3>
              </div>
              <ul className="space-y-4">
                <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    Automatische Programme
                  </h4>
                  <p className="text-xs text-slate-500">
                    10 Programme für verschiedene Schwerpunkte und
                    Entspannungsziele.
                  </p>
                </li>
                <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    Individuelle Modi
                  </h4>
                  <p className="text-xs text-slate-500">
                    5 manuelle Programme für eigene Steuerung von Intensität und
                    Position.
                  </p>
                </li>
                <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    Behandlungsdauer
                  </h4>
                  <p className="text-xs text-slate-500">
                    Standard 36:30 Min. Spezialprogramm 75 Min für
                    Tiefenentspannung.
                  </p>
                </li>
              </ul>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center text-purple-600">
                  <Music2 className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">
                  Zusatz-Funktionen
                </h3>
              </div>
              <ul className="space-y-4">
                <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    CERAGEM Sound
                  </h4>
                  <p className="text-xs text-slate-500">
                    Naturklänge &amp; klassische Musik über Lautsprecher oder
                    Kopfhörer.
                  </p>
                </li>
                <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    Audio-Assistentin
                  </h4>
                  <p className="text-xs text-slate-500">
                    Sprachführung unterstützt bei Bedienung und informiert über
                    Ablauf.
                  </p>
                </li>
                <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    Sliding-Funktion
                  </h4>
                  <p className="text-xs text-slate-500">
                    Verwandelt Liege in Ottomane – platzsparendes Design.
                  </p>
                </li>
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center text-orange-600">
                  <Activity className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">
                  Anwendungsbereiche
                </h3>
              </div>
              <ul className="space-y-4">
                <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    Oberer Rücken
                  </h4>
                  <p className="text-xs text-slate-500">
                    Fokus auf Hals- und Brustwirbelsäule (Modi 5 &amp; 7).
                  </p>
                </li>
                <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    Mittlerer Rücken
                  </h4>
                  <p className="text-xs text-slate-500">
                    Intensive Behandlung der Brustwirbelsäule und
                    Thermal-Akupressur.
                  </p>
                </li>
                <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    Unterer Rücken
                  </h4>
                  <p className="text-xs text-slate-500">
                    Gezielt für Lendenwirbel und Kreuzbein (Modus 6).
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
    {/* Pricing Section */}
    <section id="video" className="py-24 bg-slate-900 text-white">
      <div className="max-w-5xl mx-auto px-6 text-center">
        <span className="text-[#D06C60] font-semibold tracking-wider text-xs uppercase mb-4 block">
          Funktionsweise
        </span>
        <h2 className="text-3xl md:text-4xl font-semibold mb-10 tracking-tight text-white">
          Die Technologie in Aktion
        </h2>
        <div className="relative aspect-video w-full rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black">
          <iframe
            title="Ceragem Presentation"
            height="100%"
            width="100%"
            src="https://tube.grundworks.de/videos/embed/1MUW1rLNi1rzmrFn5vxEi3"
            allow="fullscreen"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            style={{ border: 0 }}
            className="w-full h-full"
          />
        </div>
        <p className="mt-8 text-slate-400 max-w-2xl mx-auto leading-relaxed">
          Erfahre in diesem Video, wie der Ceragem Master V4 deine
          Wirbelsäule scannt und eine perfekt abgestimmte Massage durchführt.
        </p>
      </div>
    </section>
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-3xl font-semibold text-slate-900 mb-12">
          Der einfache Weg zu uns
        </h2>
        <div className="grid md:grid-cols-3 gap-8 relative">
          <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-slate-100 -z-10 transform -translate-y-1/2" />
          <div className="relative bg-white p-4">
            <div className="w-20 h-20 mx-auto bg-slate-50 border-4 border-white shadow-lg rounded-full flex items-center justify-center text-2xl font-bold text-[#D06C60] mb-6">
              1
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Einfach anrufen
            </h3>
            <p className="text-sm text-slate-500">
              Ruf uns einfach an oder schreib uns eine Nachricht.
            </p>
          </div>
          <div className="relative bg-white p-4">
            <div className="w-20 h-20 mx-auto bg-slate-50 border-4 border-white shadow-lg rounded-full flex items-center justify-center text-2xl font-bold text-[#D06C60] mb-6">
              2
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Termin erhalten
            </h3>
            <p className="text-sm text-slate-500">
              Wir vereinbaren mit Dir Deinen individuellen Termin.
            </p>
          </div>
          <div className="relative bg-white p-4">
            <div className="w-20 h-20 mx-auto bg-[#D06C60] border-4 border-white shadow-lg rounded-full flex items-center justify-center text-2xl font-bold text-white mb-6">
              3
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Vorbeikommen &amp; entspannen
            </h3>
            <p className="text-sm text-slate-500">
              Komm in unsere Praxis und entspanne endlich so richtig!
            </p>
          </div>
        </div>
        <div className="mt-12">
          <ButtonDark
            onClick={() => window.location.href = '#termin'}
          >
            Jetzt Termin anfragen
            <ArrowRight className="w-4 h-4" />
          </ButtonDark>
        </div>
      </div>
    </section>
    <section id="preise" className="py-20 bg-slate-50">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight mb-4">
            Investiere in deine Gesundheit
          </h2>
          <p className="text-slate-500">
            Transparente Preise. Keine versteckten Kosten. Volle Entspannung.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          {/* Standard Card */}
          <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm hover:shadow-md transition-all">
            <h3 className="text-xl font-semibold text-slate-900">
              Kennenlern-Behandlung
            </h3>
            <p className="text-slate-500 text-sm mt-2">
              Ideal zum Ausprobieren der Wirkung.
            </p>
            <div className="my-8">
              <span className="text-4xl font-bold text-slate-900">20€</span>
              <span className="text-slate-500">/ 40 Min</span>
            </div>
            <ul className="space-y-3 mb-8 text-sm text-slate-600">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-slate-400" />
                Kurzes Anamnese-Gespräch
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-slate-400" />
                Voller Wirbelsäulen-Scan
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-slate-400" />
                Massage &amp; Infrarotwärme
              </li>
            </ul>
            <ButtonDark
              onClick={() => window.location.href = '#termin'}
            >
              Termin vereinbaren
            </ButtonDark>
          </div>
          {/* Highlight Card */}
          <div className="bg-white p-8 rounded-3xl border-2 border-[#D06C60] shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-[#D06C60] text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl uppercase tracking-wider">
              Beliebt
            </div>
            <h3 className="text-xl font-semibold text-slate-900">12er Karte</h3>
            <p className="text-slate-500 text-sm mt-2">
              Für nachhaltige Linderung &amp; Prävention.
            </p>
            <div className="my-8 flex items-end gap-2">
              <span className="text-4xl font-bold text-slate-900">200€</span>
              <span className="text-slate-500 line-through mb-1 text-sm">
                240€
              </span>
            </div>
            <ul className="space-y-3 mb-8 text-sm text-slate-600">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#D06C60]" />
                <strong>Ersparnis von 40€</strong>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#D06C60]" />
                Übertragbar auf Familienmitglieder
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#D06C60]" />
                Gültigkeit 12 Monate
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#D06C60]" />
                Inkl. Getränk nach Wahl
              </li>
            </ul>
            <ButtonDark
              onClick={() => window.location.href = '#termin'}
            >
              10er Karte sichern
            </ButtonDark>
            <p className="text-left text-xs text-slate-400 mt-4">
              15€ pro Anwendung
            </p>
          </div>
        </div>
      </div>
    </section>
    {/* Contact Form Section */}
    <section id="termin" className="py-24 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-[#D06C60] font-semibold tracking-wider text-xs uppercase mb-4 block">
            Termin vereinbaren
          </span>
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight mb-4">
            Jetzt Kontakt aufnehmen
          </h2>
          <p className="text-slate-500 text-lg max-w-2xl mx-auto">
            Fülle das Formular aus und wir melden uns schnellstmöglich bei
            dir zurück, um einen passenden Termin zu vereinbaren.
          </p>
        </div>
        <div className="bg-slate-50 rounded-3xl p-8 md:p-12 border border-slate-200 shadow-sm">
          <ContactForm />
        </div>
        <div className="mt-12 text-center">
          <p className="text-slate-500 text-sm mb-4">
            Oder kontaktiere uns direkt:
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <ButtonDark onClick={() => window.location.href = 'tel:+49369584196'}>
              <Phone className="w-4 h-4" />
              03695 / 84196
            </ButtonDark>
            <ButtonLight onClick={() => window.location.href = 'mailto:info@gesundheit-macht-sinn.de'}>
              E-Mail senden
            </ButtonLight>
          </div>
        </div>
      </div>
    </section>
    {/* Testimonials */}
    <section className="py-24 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-2xl font-semibold text-slate-900 text-center mb-16">
          Das sagen unsere Patienten
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Review 1 */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <div className="flex items-center gap-1 text-yellow-400 mb-4">
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
            </div>
            <p className="text-slate-600 text-sm leading-relaxed mb-6">
              "Ich war skeptisch, aber nach der dritten Anwendung waren meine
              unteren Rückenschmerzen deutlich besser. Die Wärme ist unglaublich
              angenehm."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                MK
              </div>
              <span className="text-sm font-medium text-slate-900">
                Michael K.
              </span>
            </div>
          </div>
          {/* Review 2 */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <div className="flex items-center gap-1 text-yellow-400 mb-4">
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
            </div>
            <p className="text-slate-600 text-sm leading-relaxed mb-6">
              "Endlich etwas, das ich in meiner Mittagspause machen kann, ohne
              mich umzuziehen. Sehr saubere Praxis in Bad Salzungen."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                SB
              </div>
              <span className="text-sm font-medium text-slate-900">
                Sabine B.
              </span>
            </div>
          </div>
          {/* Review 3 */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <div className="flex items-center gap-1 text-yellow-400 mb-4">
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
            </div>
            <p className="text-slate-600 text-sm leading-relaxed mb-6">
              "Das Team ist super freundlich und erklärt die Technik genau. Ich
              habe mir direkt die 10er Karte geholt."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                TR
              </div>
              <span className="text-sm font-medium text-slate-900">
                Thomas R.
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
    {/* FAQ Section */}
    <section id="faq" className="py-20 bg-slate-50">
      <div className="max-w-3xl mx-auto px-6">
        <h2 className="text-3xl font-semibold text-slate-900 mb-10 text-center tracking-tight">
          Häufig gestellte Fragen
        </h2>
        <div className="space-y-4">
          <details className="group bg-white rounded-xl border border-slate-200 open:border-slate-300 transition-colors">
            <summary className="flex items-center justify-between p-6 cursor-pointer font-medium text-slate-900">
              Brauche ich einen Termin?
              <ChevronDown className="w-5 h-5 text-slate-400 transition-transform group-open:rotate-180" />
            </summary>
            <div className="px-6 pb-6 text-slate-600 leading-relaxed text-sm">
              Ja, um Wartezeiten zu vermeiden, bitten wir um eine vorherige
              Terminvereinbarung. Dies kannst du telefonisch oder über unsere
              Online-Anfrage tun.
            </div>
          </details>
          <details className="group bg-white rounded-xl border border-slate-200 open:border-slate-300 transition-colors">
            <summary className="flex items-center justify-between p-6 cursor-pointer font-medium text-slate-900">
              Übernimmt die Krankenkasse die Kosten?
              <ChevronDown className="w-5 h-5 text-slate-400 transition-transform group-open:rotate-180" />
            </summary>
            <div className="px-6 pb-6 text-slate-600 leading-relaxed text-sm">
              Die Ceragem-Behandlung ist eine Privatleistung und wird in der
              Regel nicht von gesetzlichen Kassen übernommen. Einige private
              Zusatzversicherungen übernehmen jedoch Heilpraktiker-Leistungen
              oder alternative Methoden.
            </div>
          </details>
          <details className="group bg-white rounded-xl border border-slate-200 open:border-slate-300 transition-colors">
            <summary className="flex items-center justify-between p-6 cursor-pointer font-medium text-slate-900">
              Wie oft sollte ich die Behandlung nutzen?
              <ChevronDown className="w-5 h-5 text-slate-400 transition-transform group-open:rotate-180" />
            </summary>
            <div className="px-6 pb-6 text-slate-600 leading-relaxed text-sm">
              Für akute Beschwerden empfehlen wir 2-3 Anwendungen pro Woche. Zur
              Prävention und Entspannung reicht oft 1 Anwendung pro Woche. Wir
              erstellen gerne einen individuellen Plan für dich.
            </div>
          </details>
        </div>
      </div>
    </section>
    {/* CTA & Map Section */}
    <section className="py-24 px-6 max-w-7xl mx-auto">
      <div className="bg-[#2D3748] rounded-3xl overflow-hidden shadow-2xl flex flex-col lg:flex-row relative">
        {/* Decorative glow */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#D06C60] opacity-10 blur-[100px] rounded-full pointer-events-none" />
        <div className="p-10 lg:p-16 flex-1 flex flex-col justify-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6 tracking-tight">
            Bereit für deine Auszeit?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-md">
            Besuche uns in der Physiotherapie Sinn in Bad Salzungen. Vereinbare
            noch heute deinen ersten Termin.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <ButtonDark onClick={() => window.location.href = 'tel:+49369584196'}>
              Jetzt anrufen: 03695 / 84196
            </ButtonDark>
            <ButtonLight onClick={() => window.location.href = '#termin'}>
              Online anfragen
            </ButtonLight>
          </div>
        </div>
        <div className="lg:w-[45%] bg-slate-800 relative min-h-[300px]">
          {/* Placeholder Map */}
          <img
            src="https://images.unsplash.com/photo-1524661135-423995f22d0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
            className="absolute inset-0 w-full h-full object-cover opacity-60 mix-blend-overlay grayscale"
            alt="Map"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-white p-4 rounded-xl shadow-xl flex items-center gap-3">
              <div className="w-10 h-10 bg-[#D06C60] rounded-full flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-semibold uppercase">
                  Standort
                </p>
                <p className="text-sm font-semibold text-slate-900">
                  Bad Salzungen
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    {/* Footer */}
    <footer className="bg-white border-t border-slate-100 py-16 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-12 mb-16">
          {/* Col 1 */}
          <div>
            <h4 className="font-bold text-slate-900 mb-6">Home</h4>
            <ul className="space-y-3 text-sm text-slate-500">
              <li>
                <a href="#" className="hover:text-[#D06C60]">
                  Start
                </a>
              </li>
              <li>
                <a href="#vorteile" className="hover:text-[#D06C60]">
                  Warum wir?
                </a>
              </li>
              <li>
                <a href="#ceragem" className="hover:text-[#D06C60]">
                  Unser Konzept
                </a>
              </li>
              <li>
                <a href="#preise" className="hover:text-[#D06C60]">
                  Privatleistungen
                </a>
              </li>
              <li>
                <a href="#video" className="hover:text-[#D06C60]">
                  Video
                </a>
              </li>
            </ul>
          </div>
          {/* Col 2 */}
          <div>
            <h4 className="font-bold text-slate-900 mb-6">Die Praxis</h4>
            <ul className="space-y-3 text-sm text-slate-500">
              <li>
                <a href="#ceragem" className="hover:text-[#D06C60]">
                  Konzept Details
                </a>
              </li>
              <li>
                <a href="#vorteile" className="hover:text-[#D06C60]">
                  Behandlungen
                </a>
              </li>
              <li>
                <a href="#faq" className="hover:text-[#D06C60]">
                  Häufige Fragen
                </a>
              </li>
              <li>
                <a href="#termin" className="hover:text-[#D06C60]">
                  Termin vereinbaren
                </a>
              </li>
            </ul>
          </div>
          {/* Col 3 */}
          <div>
            <h4 className="font-bold text-slate-900 mb-6">Ceragem</h4>
            <ul className="space-y-3 text-sm text-slate-500">
              <li>
                <a href="#ceragem" className="hover:text-[#D06C60]">
                  Hightech &amp; Entspannung
                </a>
              </li>
              <li>
                <a href="#video" className="hover:text-[#D06C60]">
                  Video ansehen
                </a>
              </li>
              <li>
                <a href="#termin" className="hover:text-[#D06C60]">
                  Jetzt testen
                </a>
              </li>
            </ul>
          </div>
          {/* Col 4 */}
          <div>
            <h4 className="font-bold text-slate-900 mb-6">Leistungen</h4>
            <ul className="space-y-3 text-sm text-slate-500">
              <li>
                <a href="#ceragem" className="hover:text-[#D06C60]">
                  Ceragem Master V4
                </a>
              </li>
              <li>
                <a href="#vorteile" className="hover:text-[#D06C60]">
                  Vorteile &amp; Features
                </a>
              </li>
              <li>
                <a href="#preise" className="hover:text-[#D06C60]">
                  Preise
                </a>
              </li>
              <li>
                <a href="#termin" className="hover:text-[#D06C60]">
                  Termin buchen
                </a>
              </li>
            </ul>
          </div>
          {/* Col 5 */}
          <div>
            <h4 className="font-bold text-slate-900 mb-6">Kontakt</h4>
            <ul className="space-y-3 text-sm text-slate-500">
              <li>Bad Salzungen</li>
              <li>
                <a href="tel:+49369584196" className="hover:text-[#D06C60]">
                  03695 / 84196
                </a>
              </li>
              <li>
                <a href="mailto:info@gesundheit-macht-sinn.de" className="hover:text-[#D06C60]">
                  Email senden
                </a>
              </li>
              <li>
                <a href="#termin" className="hover:text-[#D06C60]">
                  Termin vereinbaren
                </a>
              </li>
            </ul>
          </div>
          {/* Col 6 */}
          <div>
            <h4 className="font-bold text-slate-900 mb-6">Rechtliches</h4>
            <ul className="space-y-3 text-sm text-slate-500">
              <li>
                <a href="#" className="hover:text-[#D06C60]">
                  Impressum
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-[#D06C60]">
                  Datenschutz
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-[#D06C60]">
                  Cookie-Einstellungen
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-100 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-400 text-sm">
            Qualifizierte Physiotherapie für Dich – kompetent, ganzheitlich und
            nah an Deinen Bedürfnissen.
          </p>
          <p className="text-slate-400 text-xs">
            Created by www.grundworks.de - 2025
          </p>
        </div>
      </div>
    </footer>
  </main>
  {/* Floating UI Elements */}
  {/* Cookie Settings (Bottom Left) */}
  <div className="fixed bottom-4 left-4 z-40 hidden md:block">
    <button className="bg-white/90 backdrop-blur border border-slate-200 text-slate-600 text-[10px] font-semibold px-3 py-2 rounded-lg shadow-sm hover:bg-slate-50 transition-colors uppercase tracking-wide">
      Cookie Einstellungen
    </button>
  </div>
  {/* Sticky Mobile CTA (Bottom) - Only visible on mobile */}
  <div className="fixed bottom-0 left-0 w-full p-4 bg-white border-t border-slate-100 md:hidden z-50">
    <a
      onClick={() => window.location.href = '#termin'}
      className="block w-full bg-[#D06C60] text-white text-center font-medium py-3 rounded-lg shadow-lg"
    >
      Termin buchen
    </a>
  </div>
</>
);
}

export default HomePage;